"use client";

import React, { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button/Button";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { ChatTaskProposalEmbed } from "./types";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { useOpenWindow } from "@/components/ui/os-layout/useWindow";
import { openTaskInMissionControl } from "./openTaskInMissionControl";

type TaskProposalCardProps = {
  embed: ChatTaskProposalEmbed;
  messageId?: string;
  embedIndex?: number;
};

type CreatedState = { taskId: string };

export function TaskProposalCard({ embed, messageId, embedIndex }: TaskProposalCardProps) {
  const navigate = useNavigate();
  const osActions = useOsActionsOptional();
  const openWindow = useOpenWindow();
  const inOsLayout = Boolean(osActions);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optimistic, setOptimistic] = useState<CreatedState | null>(null);

  const created: CreatedState | null =
    optimistic ?? (embed.created_task_id ? { taskId: embed.created_task_id } : null);

  const openTask = (taskId: string) => {
    openTaskInMissionControl(taskId, { navigate, openWindow, inOsLayout });
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await AgenticV2API.createTask({
        catalogue_id: embed.catalogue_id,
        entity_type: "fa_client",
        entity_id: embed.fa_client_id,
        mission_id: embed.mission_id ?? null,
        ...(embed.proposed_args && Object.keys(embed.proposed_args).length > 0
          ? { inputs_resolved: embed.proposed_args }
          : {}),
        source_message_id: messageId,
        source_embed_index: embedIndex,
      });
      setOptimistic({ taskId: result.task_id });
      openTask(result.task_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-lg border border-amber-200 bg-amber-50/80 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30"
      data-testid="chat-task-proposal"
    >
      <p className="font-medium text-slate-900 dark:text-slate-100">{embed.label}</p>
      {embed.why ? (
        <p className="mt-1 text-slate-600 dark:text-slate-400">{embed.why}</p>
      ) : null}
      {error ? <p className="mt-2 text-red-600 text-xs">{error}</p> : null}
      {created ? (
        <div className="mt-3">
          <Button outline onClick={() => openTask(created.taskId)}>
            View task
          </Button>
        </div>
      ) : (
        <Button
          className="mt-3"
          onClick={() => void handleCreate()}
          disabled={loading}
        >
          {loading ? "Creating…" : "Create task"}
        </Button>
      )}
    </div>
  );
}
